import { useTranslation } from 'react-i18next'
import { useInView } from '@/hooks/use-in-view'
import { Card } from '@/components/ui/card'

interface MetricCardProps {
  valueKey: string
  labelKey: string
  delay: number
}

function MetricCard({ valueKey, labelKey, delay }: MetricCardProps) {
  const { t } = useTranslation('landing')
  const [ref, inView] = useInView<HTMLDivElement>({ threshold: 0.3 })

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <Card variant="glass" className="p-8 text-center space-y-2 hover:scale-105 transition-transform">
        <div className="text-4xl md:text-5xl font-bold text-gradient-primary">
          {t(valueKey)}
        </div>
        <div className="text-gray-400 text-sm font-medium uppercase tracking-wider">
          {t(labelKey)}
        </div>
      </Card>
    </div>
  )
}

const metrics = [
  { valueKey: 'socialProof.metrics.agents.value', labelKey: 'socialProof.metrics.agents.label' },
  { valueKey: 'socialProof.metrics.workflows.value', labelKey: 'socialProof.metrics.workflows.label' },
  { valueKey: 'socialProof.metrics.hours.value', labelKey: 'socialProof.metrics.hours.label' },
  { valueKey: 'socialProof.metrics.uptime.value', labelKey: 'socialProof.metrics.uptime.label' },
]

export function SocialProof() {
  const { t } = useTranslation('landing')
  const [ref, inView] = useInView<HTMLDivElement>({ threshold: 0.1 })

  return (
    <section className="relative py-24 px-4">
      <div className="max-w-6xl mx-auto">
        <div
          ref={ref}
          className={`text-center max-w-3xl mx-auto mb-16 space-y-4 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white">{t('socialProof.title')}</h2>
          <p className="text-lg text-gray-400">{t('socialProof.subtitle')}</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric, index) => (
            <MetricCard
              key={metric.valueKey}
              valueKey={metric.valueKey}
              labelKey={metric.labelKey}
              delay={index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
